import { Color, Spacing } from '@persona-id/design-tokens';
import { CircleFillIcon, CircleNullIcon } from '@persona-id/design-tokens/icons';
import { isEmpty } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import Divider from 'dashboard/components/Divider';
import Permissioned from 'dashboard/components/Permissioned';
import ReportDismissModal from 'dashboard/components/ReportResult/ReportDismissModal';
import ComparisonChecksTable, {
  ComparisonCheckRow,
} from 'dashboard/components/VerificationDetail/VerificationTypes/DatabaseVerification/ComparisonChecksTable';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import {
  CustomListComparisonMatchType,
  ReportRun,
  ReportRunCustomList,
  ReportRunCustomListDismissPayload,
  ReportRunCustomListHitEntry,
} from 'dashboard/models/report-run';
import { showMessage } from 'dashboard/redux/actions/messages';
import { setReportRun } from 'dashboard/redux/actions/report-runs';
import { setCaseObjects } from 'dashboard/redux/slices/case';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Heading, Paragraph } from 'design-system/components/Text';
import { HelperTooltip } from 'design-system/components/Tooltip';
import { BASE } from 'design-system/variables/spacing';
import { FontWeight } from 'design-system/variables/text';
import { DASHED_DATE_FORMAT } from 'lib/constants/luxonFormats';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';
import { Iso8601DateString } from 'lib/types';

interface HitDetailsProps {
  hit: ReportRunCustomListHitEntry;
  reportRun?: ReportRunCustomList;
  versionId: string;
}

export const HitDetails = ({ hit, reportRun, versionId }: HitDetailsProps) => {
  const caseToken = useSelector((state: RootState) => {
    return state.case.caseToken;
  });
  const caseObjects = useSelector((state: RootState) => {
    return caseToken ? state.case.case[caseToken].caseDetails?.objects : [];
  });
  const dispatch = useDispatch();
  const currentEnvironment = useSelector(
    (state: RootState) => state.currentEnvironment.currentEnvironment,
  );
  const [dismissOpen, { enable: showDismiss, disable: hideDismiss }] = useToggle(false);

  const { error: dismissError, callback: handleDismiss } = useApiCallback(
    async (reason: string, rowNumber: number) => {
      if (reportRun) {
        const { data } = await SessionClientDashboard.postResourceAction<
          ReportRun,
          ReportRunCustomListDismissPayload
        >(
          {
            id: reportRun.id,
            type: reportRun.type,
            attributes: {
              'row-number': rowNumber,
              reason: reason,
            },
          },
          'dismiss',
        );
        dispatch(setReportRun(data, currentEnvironment));
        if (caseObjects && caseToken) {
          dispatch(
            setCaseObjects({
              key: caseToken,
              objects: caseObjects.map((o) => (o.id === data.id ? data : o)),
            }),
          );
        }
        dispatch(showMessage(`Successfully dismissed!`, MessageType.Success));
      }
    },
    [hit],
  );

  useApiErrorHandler({
    error: dismissError,
    onError: () =>
      dispatch(showMessage('Failed to Dismiss Matches, please try again.', MessageType.Failure)),
    onNetworkError: () =>
      dispatch(showMessage('Failed to Dismiss Matches, please try again.', MessageType.Failure)),
  });

  const dismissed = !isEmpty(hit.dismissal);
  const canDismiss = !dismissed && !isEmpty(reportRun);
  const reportTemplateToken = reportRun?.relationships['report-template'].data.id;
  const reportTemplateVersionToken = reportRun?.relationships['report-template-version'].data.id;

  const rowNumber = hit['match-group-results']
    ? hit['row-number']
    : hit['comparison-detail']['row-number'];

  const onSubmit = async (reason: string) => {
    await handleDismiss(reason, rowNumber);
  };

  return (
    <>
      {canDismiss && (
        <ReportDismissModal
          isOpen={dismissOpen}
          onClose={hideDismiss}
          onSubmit={onSubmit}
          header='Dismiss entity match?'
        />
      )}
      <TitleContainer>
        <Card.Title>Entity Match</Card.Title>
        {canDismiss && (
          <Permissioned permission={Permission.ReportIgnoreHitsWrite}>
            <Button
              id='report-run-custom-list__dismiss'
              variant='basic'
              width='auto'
              disabled={false}
              onClick={showDismiss}
            >
              Dismiss
            </Button>
          </Permissioned>
        )}
      </TitleContainer>
      {versionId && (
        <Paragraph>
          Matched from Row {`${rowNumber}`} of file from version{' '}
          {reportRun ? (
            <Link
              to={`/dashboard/report-templates/${reportTemplateToken}/${reportTemplateVersionToken}`}
            >
              {versionId}
            </Link>
          ) : (
            versionId
          )}
        </Paragraph>
      )}
      {hit['match-group-results'] ? (
        <>
          {hit['match-group-results'].map((matchGroupResult) => {
            if (matchGroupResult['match-requirement-results']) {
              const comparisonCheckRows: ComparisonCheckRow[] = matchGroupResult[
                'match-requirement-results'
              ].map((matchRequirementResult) => {
                return {
                  data: {
                    status: matchRequirementResult.status,
                    property: matchRequirementResult.properties[0],
                    /** TODO: fix the typing of claimed-attributes to not be unknown  */
                    submitted:
                      (matchRequirementResult['claimed-attributes'][0] as string) ?? undefined,
                    /** TODO: fix the typing of extracted-attributes to not be unknown  */
                    record:
                      (matchRequirementResult['extracted-attributes'][0] as string) ?? undefined,
                    matchResult: matchRequirementResult['best-result'],
                  },
                };
              });

              return (
                <>
                  <MatchGroupHeading key='match-group-heading' size='sm'>
                    {matchGroupResult.description} Result:
                  </MatchGroupHeading>
                  <ComparisonChecksTable
                    key='comparison-check-table'
                    rows={comparisonCheckRows}
                    columns={['status', 'property', 'submitted', 'record', 'matchResult']}
                  />
                  <Spacer key='spacer' height={2} />
                </>
              );
            } else {
              return null;
            }
          })}
        </>
      ) : (
        <>
          {dismissed && (
            <>
              <StyledDivider full={true} />
              <h4>Dismissal reason</h4>
              <Paragraph>{hit.dismissal?.reason}</Paragraph>
              <Paragraph>
                {[
                  DateTime.fromISO(hit.dismissal?.['dismissed-at'] as Iso8601DateString).toFormat(
                    DASHED_DATE_FORMAT,
                  ),
                  hit.dismissal?.['creator-name'],
                ]
                  .filter(Boolean)
                  .join(' • ')}
              </Paragraph>
            </>
          )}
          <Spacer height={1} />
          <TableWrapper>
            <Table size='sm'>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Property</TableHeaderCell>
                  <TableHeaderCell>Source (file)</TableHeaderCell>
                  <TableHeaderCell>Submitted (account)</TableHeaderCell>
                  <StyledTableHeaderCell>
                    Match result
                    <HelperTooltip size='sm' side='top'>
                      <HelperText>
                        <dl>
                          <dt>Full match:</dt>
                          <dd>
                            The submitted input matches the information from the source file
                            exactly.
                          </dd>
                          <dt>Partial match:</dt>
                          <dd>
                            The submitted input matches the information from the source file
                            partially, including some typos.
                          </dd>
                          <dt>No match:</dt>
                          <dd>
                            The submitted input does not match the information from the source file.
                          </dd>
                          <dt>N/A:</dt>
                          <dd>There was not enough information to do a comparison.</dd>
                        </dl>
                      </HelperText>
                    </HelperTooltip>
                  </StyledTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(hit['comparison-detail'] ?? {})
                  .map((key, idx) => {
                    const detail = hit['comparison-detail'][key];
                    return detail == null ||
                      (detail['query-value'] == null &&
                        detail['custom-list-record-value'] == null) ||
                      key === 'row-number' ? (
                      false
                    ) : (
                      <TableRow key={`${idx}-${key}`}>
                        <TableCell>{detail['display-property']}</TableCell>
                        <TableCell style={{ maxWidth: '150px', whiteSpace: 'normal' }}>
                          {detail['custom-list-record-value']}
                        </TableCell>
                        <TableCell style={{ maxWidth: '150px', whiteSpace: 'normal' }}>
                          {detail['query-value']}
                        </TableCell>
                        <TableCell>
                          {getMatchLabel(
                            detail.status,
                            detail['query-value'] != null &&
                              detail['custom-list-record-value'] != null,
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                  .filter(Boolean)}
              </TableBody>
            </Table>
          </TableWrapper>
        </>
      )}
    </>
  );
};

const getMatchLabel = (status: CustomListComparisonMatchType, hasBothValues: boolean) => {
  let displayText = 'N/A';
  let icon = <CircleNullIcon size='16px' color={Color.CoreSlate800} />;

  if (status === CustomListComparisonMatchType.Full) {
    displayText = 'Full match';
    icon = <CircleFillIcon size='16px' color={Color.CoreSlate800} />;
  } else if (status === CustomListComparisonMatchType.Partial) {
    displayText = 'Partial match';
    icon = <HalfCircle />;
  } else if (hasBothValues && status === CustomListComparisonMatchType.NoMatch) {
    displayText = 'No match';
  }

  return (
    <MatchLabel>
      {icon}
      <span>{displayText}</span>
    </MatchLabel>
  );
};

const HalfCircle = () => {
  return (
    <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <g clipPath='url(#clip0_3670_71623)'>
        <path
          d='M7.5 1.35184C4.05159 1.60748 1.33325 4.48631 1.33325 8.00004C1.33325 11.5138 4.05159 14.3926 7.5 14.6482V1.35184Z'
          fill={Color.CoreSlate800}
        />
        <path
          d='M7.5 1.35184C7.66504 1.3396 7.83175 1.33337 7.99992 1.33337C11.6818 1.33337 14.6666 4.31814 14.6666 8.00004C14.6666 11.682 11.6818 14.6667 7.99992 14.6667C7.83175 14.6667 7.66504 14.6605 7.5 14.6482M7.5 1.35184C4.05159 1.60748 1.33325 4.48631 1.33325 8.00004C1.33325 11.5138 4.05159 14.3926 7.5 14.6482M7.5 1.35184V14.6482'
          stroke={Color.CoreSlate800}
          strokeWidth='1.25'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </g>
      <defs>
        <clipPath id='clip0_3670_71623'>
          <rect width='16' height='16' fill={Color.BackgroundPrimary} />
        </clipPath>
      </defs>
    </svg>
  );
};

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const HelperText = styled.span`
  max-width: 300px;

  & dt {
    font-weight: ${FontWeight.Medium};
  }
`;

const StyledTableHeaderCell = styled(TableHeaderCell)`
  & > div {
    column-gap: ${Spacing[1]};
  }
`;

const MatchLabel = styled.span`
  display: flex;
  align-items: flex-end;
  column-gap: ${BASE / 1.5}px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const StyledDivider = styled(Divider)`
  margin-top: 0px;
  margin-bottom: 0px;
`;

const MatchGroupHeading = styled(Heading)`
  font-size: 16px;
`;
